/**
 * i18n bootstrap, minimum-viable internationalization scaffold for
 * Isaiah's World. Supports: en (default) + es (Spanish, the
 * second-most-spoken K-12 home language in the US per NCES). Adding
 * more locales is just dropping a new JSON in `./locales/{lang}.json`
 * and adding it to the resources map below.
 *
 * Usage:
 *    import { useTranslation } from "react-i18next";
 *    const { t } = useTranslation();
 *    <h1>{t("home.welcome")}</h1>
 *
 * The detector cascade:
 *    1. localStorage `i18nextLng` (set by user-facing language picker)
 *    2. browser navigator.language
 *    3. HTML <html lang>
 *    4. fallback: `en`
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import es from "./locales/es.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: { escapeValue: false }, // React handles XSS
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
  });

// Keep the <html lang="..."> attribute in sync with the active locale
// for screen readers + SEO crawlers + spell-check engines.
const _syncHtmlLang = (lng) => {
  try {
    if (typeof document !== "undefined") document.documentElement.lang = lng || "en";
  } catch { /* SSR / no-op */ }
};
_syncHtmlLang(i18n.language);
i18n.on("languageChanged", _syncHtmlLang);

export default i18n;
